import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { FaSave } from "react-icons/fa";
import { Button } from 'components/common';
import { getSigners, updateSigners, updateSignerOptions, searchUsers } from "../common/settingsApi";
import UserPicker from "../common/UserPicker";
import MultiUserPicker from "../common/MultiUserPicker";
import { TH, TD, TONE, Chip, SettingsCard } from "./settingsKit";

// Team text under the picker of a team role (everyone listed can work the step).
const TEAM_TEXT = {
  accountant: {
    add: "Add an accountant...", empty: "Select accountants...",
    main: "requesters pick one of them as Accountant in charge; any of them can work the step.",
    none: "The first person is the main accountant; requesters pick one of them as Accountant in charge.",
  },
  payment_accountant: {
    add: "Add a payment accountant...", empty: "Select payment accountants...",
    main: "any of them can pay a request after BOD approves.",
    none: "Everyone listed is notified and can pay a request after BOD approves.",
  },
};

// Options saved on their own, straight away — they do not touch the signers above.
const OPTIONS = [
  { key: "admin_act_as_accountant", title: "Admins can act at the Accountant step",
    text: "An admin may create the journals in ERP, prepare the HSBC file and approve or reject in place of the Accountant — only at that step. Each such action is recorded as an admin override on the request.",
    on: "Admins can now act at the Accountant step.", off: "Only the Accountant acts at the Accountant step." },
  { key: "disbursement_review", title: "Payment Accountant handles HSBC payments",
    text: "On: after BOD approves, every HSBC payment goes to the Payment Accountant, who sends it to HSBC. Off: HSBC payments are sent automatically after BOD. Trade loan and Non-HSBC payments always go to the Payment Accountant.",
    on: "HSBC payments now go to the Payment Accountant after BOD.", off: "HSBC payments are sent automatically after BOD." },
];

const toPerson = (r) => (r?.email ? { id: r.user_id, email: r.email, name: r.name } : null);
const toAlternates = (list) => (list || []).map((a) => ({ id: a.user_id, email: a.email, name: a.name }));
const emails = (list) => (list || []).map((a) => a.email).join("|");

// "Accountant / Kế toán viên" -> ["Accountant", "Kế toán viên"]
const splitLabel = (label = "") => {
  const [en, ...rest] = label.split(" / ");
  return [en, rest.join(" / ")];
};

/** Ảnh chữ ký Hub-Sign của người ký — hoặc lý do vì sao chưa hiện được. */
const SignatureCell = ({ person, url, serviceOk, dirty }) => {
  if (!person) return <span className="tw-text-geist-fg-3">—</span>;
  if (dirty) return <Chip tone={TONE.muted}>Save to check</Chip>;
  if (!serviceOk) {
    return <Chip tone={TONE.warn} icon="bi-cloud-slash" title="Hub-Sign did not answer — this does not mean the person has no signature.">Hub-Sign down</Chip>;
  }
  if (!url) {
    return (
      <Chip tone={TONE.bad} icon="bi-exclamation-triangle"
        title="No default signature on Hub-Sign. Their step will be blocked until they create one under My signature.">
        No signature
      </Chip>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" title="Open the signature image"
      className="tw-inline-flex tw-h-10 tw-w-32 tw-items-center tw-justify-center tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-white hover:tw-border-geist-accent">
      <img src={url} alt={`Signature of ${person.name || person.email}`} className="tw-max-h-8 tw-max-w-[112px] tw-object-contain" />
    </a>
  );
};

SignatureCell.propTypes = {
  person: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  url: PropTypes.string,
  serviceOk: PropTypes.bool,
  dirty: PropTypes.bool,
};

/**
 * Cấu hình người ký cho từng vai trong chuỗi duyệt: một bảng theo đúng thứ tự ký,
 * mỗi dòng kèm ảnh chữ ký Hub-Sign (để admin thấy ngay ai chưa tạo chữ ký — tới
 * bước của người đó, approve! sẽ chặn) và vai đó nằm trong luồng nào.
 *
 * Danh sách vai, nhãn, gợi ý và vai nào cho phép ủy quyền đều lấy từ backend
 * (`ROLES` trong SettingsController) — không chép cứng ở đây, để thêm/bớt vai chỉ
 * phải sửa một chỗ. Line Manager không cấu hình ở đây: người lập chỉ định trên phiếu.
 */
const SignersPanel = () => {
  const [roles, setRoles] = useState([]);
  const [signers, setSigners] = useState({});       // role_type -> { id, email, name }
  const [alternates, setAlternates] = useState({}); // role_type -> [{ id, email, name }]
  const [serviceOk, setServiceOk] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [options, setOptions] = useState({});
  const [savingOption, setSavingOption] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getSigners();
      const rows = (res.data || []).filter((role) => role.role_type !== 'accountant_receivable');
      setRoles(rows);
      setSigners(Object.fromEntries(rows.map((r) => [r.role_type, toPerson(r)])));
      setAlternates(Object.fromEntries(rows.map((r) => [r.role_type, toAlternates(r.alternate_signers)])));
      setServiceOk(res.signature_service?.ok !== false);
      setOptions(res.options || {});
      setShowLegacy(rows.some((r) => r.legacy && r.user_id));
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to load signer configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Saved on its own, straight away — it does not touch the signers above.
  const toggleOption = async (option, on) => {
    setSavingOption(true);
    try {
      const res = await updateSignerOptions({ [option.key]: on });
      setOptions(res.options || {});
      toast.success(on ? option.on : option.off);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not save the option.");
    } finally {
      setSavingOption(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateSigners(roles.map((r) => ({
        role_type: r.role_type,
        user_id: signers[r.role_type]?.id || null,
        ...(r.delegatable || r.team ? { alternate_user_ids: (alternates[r.role_type] || []).map((a) => a.id) } : {}),
      })));
      toast.success("Signers saved.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // null = đã tra Hub-Sign, người này không có chữ ký; undefined = chưa tra (chưa lưu).
  const sigByEmail = useMemo(() => {
    const m = {};
    roles.forEach((r) => {
      if (r.email) m[r.email.toLowerCase()] = r.signature_url;
      (r.alternate_signers || []).forEach((a) => { if (a.email) m[a.email.toLowerCase()] = a.signature_url; });
    });
    return m;
  }, [roles]);

  const isDirtyRole = (r) => (signers[r.role_type]?.email || null) !== (r.email || null)
    || ((r.delegatable || r.team) && emails(alternates[r.role_type]) !== emails(r.alternate_signers));
  const dirty = roles.some(isDirtyRole);

  const active = roles.filter((r) => !r.legacy);
  const legacy = roles.filter((r) => r.legacy);
  const assigned = active.filter((r) => r.email).length;
  const noSignature = active.filter((r) => r.email && !r.signature_url).length;

  const renderRole = (r, step) => {
    const person = signers[r.role_type];
    const rowDirty = isDirtyRole(r);
    const [en, vi] = splitLabel(r.label);
    const teamText = TEAM_TEXT[r.role_type] || TEAM_TEXT.accountant;
    const missingAlternates = (alternates[r.role_type] || [])
      .filter((a) => sigByEmail[a.email?.toLowerCase()] === null);

    return (
      <React.Fragment key={r.role_type}>
        <tr className={`tw-border-0 ${r.delegatable ? "" : "tw-border-b"} tw-border-solid tw-border-geist-border ${r.legacy ? "tw-opacity-70" : ""} ${rowDirty ? "tw-bg-geist-accent/5" : ""}`}>
          <td className={`${TD} tw-w-12 tw-text-center`}>
            <span className={`tw-inline-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-bold ${
              person ? "tw-bg-geist-accent tw-text-white" : "tw-bg-geist-100 tw-text-geist-fg-3"}`}>
              {step ?? "–"}
            </span>
          </td>
          <td className={`${TD} tw-min-w-[220px]`}>
            <div className="tw-text-sm tw-font-semibold tw-text-geist-fg">
              {en}{vi && <span className="tw-font-normal tw-text-geist-fg-3"> · {vi}</span>}
              {rowDirty && <span className="tw-ml-2 tw-text-[10px] tw-font-bold tw-uppercase tw-text-geist-accent">Unsaved</span>}
            </div>
            {r.hint && <div className="tw-text-[12px] tw-text-geist-fg-3 tw-leading-snug tw-mt-0.5 tw-max-w-[420px]">{r.hint}</div>}
          </td>
          <td className={`${TD} tw-min-w-[280px] tw-w-[34%]`}>
            {r.team ? (
              <>
                {/* One box for the whole accounting team: the first person is the main
                    accountant (gets the email), everyone listed can work the step. */}
                <MultiUserPicker
                  value={[person, ...(alternates[r.role_type] || [])].filter(Boolean)}
                  onChange={(list) => {
                    setSigners((s) => ({ ...s, [r.role_type]: list[0] || null }));
                    setAlternates((a) => ({ ...a, [r.role_type]: list.slice(1) }));
                  }}
                  fetcher={searchUsers}
                  placeholder={person ? teamText.add : teamText.empty}
                />
                <div className="tw-text-[11px] tw-text-geist-fg-3 tw-mt-1">
                  {person
                    ? <>Main: <span className="tw-font-semibold tw-text-geist-fg-2">{person.name || person.email}</span> · {teamText.main}</>
                    : teamText.none}
                </div>
                {serviceOk && missingAlternates.length > 0 && (
                  <div className="tw-text-[11px] tw-text-[#8e2a18] tw-mt-1">
                    <i className="bi bi-exclamation-triangle tw-mr-1" />
                    No signature on Hub-Sign: {missingAlternates.map((a) => a.name || a.email).join(", ")}
                  </div>
                )}
              </>
            ) : (
              <UserPicker
                value={person}
                onChange={(u) => setSigners((s) => ({ ...s, [r.role_type]: u }))}
                fetcher={searchUsers}
                placeholder="Select a signer..."
              />
            )}
          </td>
          <td className={`${TD} tw-w-40`}>
            <SignatureCell person={person} url={r.signature_url} serviceOk={serviceOk}
              dirty={(person?.email || null) !== (r.email || null)} />
          </td>
        </tr>

        {/* BOD: stand-ins the Chief Accountant picks on each request. (The accounting
            team is chosen in the Accountant's own box above.) */}
        {r.delegatable && !r.team && (
          <tr className={`tw-border-0 tw-border-b tw-border-solid tw-border-geist-border ${rowDirty ? "tw-bg-geist-accent/5" : ""}`}>
            <td className={TD} />
            <td className={`${TD} tw-pt-0 tw-align-top`}>
              <div className="tw-text-[12px] tw-font-semibold tw-text-geist-fg-2 tw-pt-2.5">
                <i className="bi bi-arrow-return-right tw-mr-1 tw-text-geist-fg-3" />Stand-ins
              </div>
              <div className="tw-text-[11px] tw-text-geist-fg-3">
                Chief Accountant can pick one on each request
              </div>
            </td>
            <td className={`${TD} tw-pt-0`} colSpan={2}>
              <MultiUserPicker
                value={alternates[r.role_type] || []}
                onChange={(list) => setAlternates((a) => ({ ...a, [r.role_type]: list }))}
                fetcher={searchUsers}
                placeholder="Add a stand-in..."
              />
              {serviceOk && missingAlternates.length > 0 && (
                <div className="tw-text-[11px] tw-text-[#8e2a18] tw-mt-1">
                  <i className="bi bi-exclamation-triangle tw-mr-1" />
                  No signature on Hub-Sign: {missingAlternates.map((a) => a.name || a.email).join(", ")}
                </div>
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <SettingsCard
      title="Approval chain"
      subtitle="Who signs each step, in order. Nobody signs their own request."
      status={!loading && (
        <>
          <Chip tone={assigned === active.length ? TONE.ok : TONE.warn} icon="bi-person-check">
            {assigned}/{active.length} assigned
          </Chip>
          {serviceOk ? (
            noSignature > 0 && <Chip tone={TONE.bad} icon="bi-pen">{noSignature} without signature</Chip>
          ) : (
            <Chip tone={TONE.warn} icon="bi-cloud-slash">Hub-Sign unreachable</Chip>
          )}
        </>
      )}
      actions={(
        <Button variant="primary" size="sm" leftIcon={FaSave} disabled={saving || loading || !dirty} onClick={save}>
          {saving ? "Saving..." : "Save signers"}
        </Button>
      )}
      footer={!loading && (
        <>
          {OPTIONS.map((o) => (
            <label key={o.key} className="tw-flex tw-items-start tw-gap-2 tw-text-[13px] tw-text-geist-fg tw-max-w-3xl tw-cursor-pointer">
              <input type="checkbox" aria-label={o.title} checked={!!options[o.key]} disabled={savingOption}
                onChange={(e) => toggleOption(o, e.target.checked)} className="tw-mt-0.5" />
              <span>
                <span className="tw-font-semibold">{o.title}</span>
                <span className="tw-block tw-text-[11px] tw-text-geist-fg-3">{o.text}</span>
              </span>
            </label>
          ))}
          <p className="tw-text-[11px] tw-text-geist-fg-3 tw-m-0 tw-max-w-3xl">
            Where a step has a stand-in, a request created by the regular signer routes to the stand-in; where it has none,
            that person cannot create requests for themselves.
          </p>
          {legacy.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowLegacy((v) => !v)}>
              {showLegacy ? "Hide retired roles" : `Show retired roles (${legacy.length})`}
            </Button>
          )}
        </>
      )}
    >
      {loading ? (
        <div className="tw-py-10 tw-text-center tw-text-geist-fg-3 tw-text-sm">Loading...</div>
      ) : (
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-border-collapse">
            <thead>
              <tr className="tw-bg-geist-bg-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
                <th className={`${TH} tw-text-center`}>Step</th>
                <th className={TH}>Role</th>
                <th className={TH}>Signer</th>
                <th className={TH}>Signature</th>
              </tr>
            </thead>
            <tbody>
              {/* Bước 1 luôn là Line Manager — người lập chọn trên phiếu, không cấu hình ở đây. */}
              <tr className="tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg-2/50">
                <td className={`${TD} tw-text-center`}>
                  <span className="tw-inline-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-geist-100 tw-text-[11px] tw-font-bold tw-text-geist-fg-3">1</span>
                </td>
                <td className={TD}>
                  <div className="tw-text-sm tw-font-semibold tw-text-geist-fg">
                    Line Manager<span className="tw-font-normal tw-text-geist-fg-3"> · direct manager</span>
                  </div>
                </td>
                <td className={`${TD} tw-text-[13px] tw-text-geist-fg-3 tw-italic`} colSpan={2}>
                  Picked by the requester on each request
                </td>
              </tr>

              {active.map((r, idx) => renderRole(r, idx + 2))}
              {showLegacy && legacy.map((r) => renderRole(r, null))}
            </tbody>
          </table>
        </div>
      )}

    </SettingsCard>
  );
};

export default SignersPanel;
